import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { ApiError, errorMessage, gebruiktMockApi, http } from './api'
import { nettoFteOp } from './aanstelling'
import {
  mockMaakAanstelling,
  mockMaakMedewerker,
  mockMedewerkers,
  mockVerwijderAanstelling,
  mockWijzigAanstelling,
  mockWijzigMedewerker,
} from './__fixtures__/beheer-fixtures'
import { medewerkerDetail, teamOverzicht } from './__fixtures__/inzet-fixtures'
import { vertraag } from './__fixtures__/vertraag'
import type {
  Aanstelling,
  AanstellingInvoer,
  Medewerker,
  MedewerkerDetail,
  MedewerkerInzet,
  MedewerkerWijziging,
  NieuweMedewerker,
  TeamOverzicht,
} from './medewerker-types'
import type { SemesterDetail } from './semester-types'

export interface MedewerkerApi {
  team(semesterId: string): Promise<TeamOverzicht>
  list(alleenActief?: boolean): Promise<Medewerker[]>
  get(semesterId: string, medewerkerId: string): Promise<MedewerkerDetail>
  maak(input: NieuweMedewerker): Promise<Medewerker>
  wijzig(medewerkerId: string, input: MedewerkerWijziging): Promise<Medewerker>
  maakAanstelling(medewerkerId: string, input: AanstellingInvoer): Promise<Aanstelling>
  wijzigAanstelling(aanstellingId: string, input: Partial<AanstellingInvoer>): Promise<Aanstelling>
  verwijderAanstelling(aanstellingId: string): Promise<void>
}

function legeInzet(medewerkerId: string, naam: string): MedewerkerInzet {
  return {
    medewerkerId,
    naam,
    lesUren: 0,
    mentoraatUren: 0,
    coachingUren: 0,
    overigeUren: 0,
    totaal: 0,
    beschikbaar: null,
    balans: null,
    bezettingPercentage: null,
    status: 'aanstelling_onbekend',
    regels: [],
  }
}

class RealMedewerkerApi implements MedewerkerApi {
  team(semesterId: string): Promise<TeamOverzicht> {
    return http.get<TeamOverzicht>(`/api/semesters/${semesterId}/team`)
  }
  list(alleenActief = false): Promise<Medewerker[]> {
    return http.get<Medewerker[]>('/api/medewerkers', alleenActief ? { actief: 1 } : undefined)
  }
  async get(semesterId: string, medewerkerId: string): Promise<MedewerkerDetail> {
    const [team, medewerker, semester] = await Promise.all([
      this.team(semesterId),
      http.get<Medewerker>(`/api/medewerkers/${medewerkerId}`),
      http.get<SemesterDetail>(`/api/semesters/${semesterId}`),
    ])

    return {
      medewerkerId,
      naam: medewerker.naam,
      email: medewerker.email,
      aanstellingFte: nettoFteOp(medewerker.aanstellingen, semester.startDatum),
      inzet:
        team.medewerkers.find((inzet) => inzet.medewerkerId === medewerkerId) ??
        legeInzet(medewerkerId, medewerker.naam),
    }
  }
  maak(input: NieuweMedewerker): Promise<Medewerker> {
    return http.post<Medewerker>('/api/medewerkers', input)
  }
  wijzig(medewerkerId: string, input: MedewerkerWijziging): Promise<Medewerker> {
    return http.patch<Medewerker>(`/api/medewerkers/${medewerkerId}`, input)
  }
  maakAanstelling(medewerkerId: string, input: AanstellingInvoer): Promise<Aanstelling> {
    return http.post<Aanstelling>(`/api/medewerkers/${medewerkerId}/aanstellingen`, input)
  }
  wijzigAanstelling(
    aanstellingId: string,
    input: Partial<AanstellingInvoer>,
  ): Promise<Aanstelling> {
    return http.patch<Aanstelling>(`/api/aanstellingen/${aanstellingId}`, input)
  }
  verwijderAanstelling(aanstellingId: string): Promise<void> {
    return http.del<void>(`/api/aanstellingen/${aanstellingId}`)
  }
}

class MockMedewerkerApi implements MedewerkerApi {
  team(semesterId: string): Promise<TeamOverzicht> {
    return vertraag(teamOverzicht(semesterId))
  }
  list(alleenActief = false): Promise<Medewerker[]> {
    return vertraag(mockMedewerkers(alleenActief))
  }
  get(semesterId: string, medewerkerId: string): Promise<MedewerkerDetail> {
    const detail = medewerkerDetail(semesterId, medewerkerId)
    if (!detail) {
      return Promise.reject(
        new ApiError(404, { error: { code: 'niet_gevonden', message: 'Medewerker niet gevonden' } }),
      )
    }
    return vertraag(detail)
  }
  maak(input: NieuweMedewerker): Promise<Medewerker> {
    return vertraag(mockMaakMedewerker(input), 160)
  }
  wijzig(medewerkerId: string, input: MedewerkerWijziging): Promise<Medewerker> {
    return vertraag(mockWijzigMedewerker(medewerkerId, input), 120)
  }
  maakAanstelling(medewerkerId: string, input: AanstellingInvoer): Promise<Aanstelling> {
    return vertraag(mockMaakAanstelling(medewerkerId, input), 160)
  }
  wijzigAanstelling(aanstellingId: string, input: Partial<AanstellingInvoer>): Promise<Aanstelling> {
    return vertraag(mockWijzigAanstelling(aanstellingId, input), 120)
  }
  verwijderAanstelling(aanstellingId: string): Promise<void> {
    mockVerwijderAanstelling(aanstellingId)
    return vertraag(undefined, 120)
  }
}

export const medewerkerApi: MedewerkerApi = gebruiktMockApi
  ? new MockMedewerkerApi()
  : new RealMedewerkerApi()

export const medewerkerKeys = {
  alle: ['medewerkers'] as const,
  team: (semesterId: string) => ['medewerkers', 'team', semesterId] as const,
  lijst: (alleenActief: boolean) => ['medewerkers', 'lijst', alleenActief] as const,
  detail: (semesterId: string, medewerkerId: string) =>
    ['medewerkers', 'detail', semesterId, medewerkerId] as const,
}

export function useTeam(semesterId: string) {
  return useQuery({
    queryKey: medewerkerKeys.team(semesterId),
    queryFn: () => medewerkerApi.team(semesterId),
    enabled: semesterId !== '',
  })
}

export function useMedewerkers(alleenActief = false) {
  return useQuery({
    queryKey: medewerkerKeys.lijst(alleenActief),
    queryFn: () => medewerkerApi.list(alleenActief),
    staleTime: 120_000,
  })
}

export function useMedewerker(semesterId: string, medewerkerId: string) {
  return useQuery({
    queryKey: medewerkerKeys.detail(semesterId, medewerkerId),
    queryFn: () => medewerkerApi.get(semesterId, medewerkerId),
    enabled: semesterId !== '' && medewerkerId !== '',
  })
}

export function useMaakMedewerker() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: NieuweMedewerker) => medewerkerApi.maak(input),
    onSuccess: (medewerker) => {
      queryClient.invalidateQueries({ queryKey: medewerkerKeys.alle })
      toast.success(`${medewerker.naam} toegevoegd`)
    },
    onError: (error) => toast.error(errorMessage(error, 'Toevoegen mislukt')),
  })
}

export function useWijzigMedewerker() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (vars: { medewerkerId: string; input: MedewerkerWijziging }) =>
      medewerkerApi.wijzig(vars.medewerkerId, vars.input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: medewerkerKeys.alle })
      toast.success('Medewerker bijgewerkt')
    },
    onError: (error) => toast.error(errorMessage(error, 'Bijwerken mislukt')),
  })
}

export function useMaakAanstelling() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (vars: { medewerkerId: string; input: AanstellingInvoer }) =>
      medewerkerApi.maakAanstelling(vars.medewerkerId, vars.input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: medewerkerKeys.alle })
      toast.success('Aanstelling toegevoegd')
    },
    onError: (error) => toast.error(errorMessage(error, 'Toevoegen mislukt')),
  })
}

export function useWijzigAanstelling() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (vars: { aanstellingId: string; input: Partial<AanstellingInvoer> }) =>
      medewerkerApi.wijzigAanstelling(vars.aanstellingId, vars.input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: medewerkerKeys.alle })
      toast.success('Aanstelling opgeslagen')
    },
    onError: (error) => toast.error(errorMessage(error, 'Opslaan mislukt')),
  })
}

export function useVerwijderAanstelling() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (aanstellingId: string) => medewerkerApi.verwijderAanstelling(aanstellingId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: medewerkerKeys.alle })
      toast.success('Aanstelling verwijderd')
    },
    onError: (error) => toast.error(errorMessage(error, 'Verwijderen mislukt')),
  })
}
